/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The comparison an address carries, as the five inputs compareMemory.ts keeps
 * (docs/plan-vergelijker-resultaten-2026-09-30.md section 12.4 B2). The Stap 1
 * card (Filters.tsx) remembers the comparison from here and the resume chip
 * decides from here whether the visitor is already looking at one, so neither
 * depends on the results engine being mounted, nor on which engine a build has.
 *
 * Every rule is the comparer's own, imported rather than copied, so the memory
 * holds exactly what the results showed:
 *
 *   audience, volume, contractDuration
 *                   readComparerInputs (comparerParams.ts), the one module the
 *                   homepage comparer and /vergelijk both read an address with.
 *   smartCharging   parseSmartChargingFilter (comparerFilter.ts), the read the
 *                   rows comparer and its filter sheet use: 'has' and the older
 *                   provider-perspective 'required' are 'has', 'none' and
 *                   'not_required' are 'none', anything else (absent, a typo) is
 *                   'all'. The old values still arrive from bookmarks, and they
 *                   mean what they meant.
 *   terms           parseTermsFilter (comparerFilter.ts): whitelisted on
 *                   FEATURE_CODES; a value off the list filters nothing there
 *                   ('all') and is "no terms filter" (null) here. The comparer
 *                   also ignores a code that no provider earns right now (its
 *                   gate, effectiveComparerFilters), which takes the rows to
 *                   know. That is a property of today's data, not of the
 *                   address, so it is not applied here: a resumed address reads
 *                   exactly as it did when it was made, and the comparer decides
 *                   again.
 *
 * "The address asked for a comparison" means it carries one of those five
 * parameters (COMPARE_FILTER_PARAMS), even with an empty or default value: the
 * comparer's own controls write them, so a visitor who set the filters back to
 * the defaults still has them in the address, and that is what lets
 * compareMemory.ts forget the comparison they undid. `estimator` is not one of
 * them (a one-shot request the card strips again), nor is the retired
 * `minRating`, nor any utm_* or click id.
 *
 * Pure: URLSearchParams in, plain values out. No React, no router, no storage.
 */

import { readComparerInputs } from './comparerParams';
import { parseSmartChargingFilter, parseTermsFilter } from './comparerFilter';
import type { CompareInputs } from './compareMemory';
import type { FeatureCode } from './providerFeatures';

/** The parameters that make an address a comparison, in the order the memory keeps them. */
export const COMPARE_FILTER_PARAMS = [
  'audience',
  'volume',
  'contractDuration',
  'smartCharging',
  'terms',
] as const;

/** True when the address carries at least one of COMPARE_FILTER_PARAMS. */
export function hasCompareFilterParam(params: URLSearchParams): boolean {
  return COMPARE_FILTER_PARAMS.some((key) => params.has(key));
}

/** `?smartCharging=` as the comparer reads it: comparerFilter.ts's own function. */
export const parseSmartCharging: (
  raw: string | null | undefined
) => CompareInputs['smartCharging'] = parseSmartChargingFilter;

/**
 * `?terms=` as the comparer reads it (comparerFilter.ts), with its "no terms
 * filter" ('all': absent, 'all', or off the list) as null.
 */
export function parseTerms(raw: string | null | undefined): FeatureCode | null {
  const terms = parseTermsFilter(raw);
  return terms === 'all' ? null : terms;
}

/** The five inputs the comparer derives from this address. */
export function compareInputsFromUrl(params: URLSearchParams): CompareInputs {
  const { audience, volume, contractDuration } = readComparerInputs(params);
  return {
    audience,
    volume,
    contractDuration,
    smartCharging: parseSmartCharging(params.get('smartCharging')),
    terms: parseTerms(params.get('terms')),
  };
}
